import {
  Checkbox,
  checkboxStates,
  GenericTable,
  GenericTableVariant,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import SelectAllCheckboxCell from "components/Cells/SelectAllCheckboxCell";
import Ellipsis from "components/Ellipsis";
import { VIRTUALIZE_TABLE } from "constants/app";
import { modalTypes } from "constants/types";
import { map, noop } from "lodash";
import {
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  string,
} from "prop-types";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { getFolderDocuments } from "slices/workspace/navigationNodes";
import { selectNavigationInfo } from "slices/workspace/selectors";

import classes from "./ChooseDocumentsModal.module.scss";

const ChooseDocumentsModal = ({
  title,
  buttonTitle,
  description,
  folderId,
  action,
  minimumCount = 0,
  maximumCount = -1,
  sorting = true,
}) => {
  const dispatch = useDispatch();
  const navigationInfo = useSelector(selectNavigationInfo);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const documents = useMemo(() => {
    return getFolderDocuments(navigationInfo, folderId) ?? [];
  }, [folderId, navigationInfo]);

  const columnsNew = useMemo(() => {
    return [
      {
        header: ({ table }) =>
          maximumCount === -1 && (
            <SelectAllCheckboxCell onUpdate={onChangeSelectAll} table={table} />
          ),

        id: "checkbox",
        accessorKey: "vtid",
        enableSorting: false,
        enableResizing: false,
        size: 40,
        cell: ({ getValue }) => {
          const vtid = getValue();
          const isSelected = selectedRowIds.includes(vtid);
          const state = isSelected
            ? checkboxStates.checked
            : checkboxStates.empty;

          return <Checkbox state={state} handleChange={noop} />;
        },
      },
      {
        header: "Document",
        accessorKey: "text",
        enableSorting: sorting,
        size: 400,
        cell: ({ getValue }) => <Ellipsis text={getValue()} />,
      },
    ];
  }, [selectedRowIds, sorting]);

  const handleOk = () => {
    const selectedDocuments = documents.filter(({ vtid }) =>
      selectedRowIds.includes(vtid)
    );

    action(selectedDocuments);
    dispatch(hideModal(modalTypes.CHOOSE_DOCUMENTS));
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.CHOOSE_DOCUMENTS));
  };

  const onClickRow = ({ vtid }) => {
    if (selectedRowIds.includes(vtid)) {
      setSelectedRowIds((state) => state.filter((id) => id !== vtid));
      return;
    }

    if (maximumCount !== -1 && selectedRowIds.length >= maximumCount) {
      if (maximumCount === 1) {
        setSelectedRowIds([vtid]);
      }
      return;
    }

    setSelectedRowIds((state) => state.concat(vtid));
  };

  function onChangeSelectAll(areAllSelected) {
    if (areAllSelected) {
      setSelectedRowIds(map(documents, "vtid"));
    } else {
      setSelectedRowIds([]);
    }
  }

  return (
    <Modal
      data-testid="choose-documents-modal"
      className="fade-in"
      show
      onDismiss={handleCancel}
      centered
    >
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        {description}

        <GenericTable
          className={classes.table}
          columns={columnsNew}
          data={documents}
          defaultSortColumnIndex={-1}
          enableRowSelection
          isVirtualized={VIRTUALIZE_TABLE}
          onRowClick={onClickRow}
          rowIdKey="vtid"
          selectedRowIds={selectedRowIds}
          variant={GenericTableVariant.bordered}
        />
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancel-button"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-testid="compare-button"
          variant={NewButtonVariant.l1}
          onClick={handleOk}
          disabled={selectedRowIds.length <= minimumCount}
        >
          {buttonTitle}
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ChooseDocumentsModal;

ChooseDocumentsModal.propTypes = {
  title: node.isRequired,
  buttonTitle: string.isRequired,
  description: oneOfType([object, string]),
  folderId: string.isRequired,
  action: func.isRequired,
  minimumCount: number,
  maximumCount: number,
  sorting: bool,
};
